import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Button } from "~/atoms/button";
import { CenterSpinner } from "~/atoms/spinner";
import { printFullName } from "~/helpers/user";
import { BulkSelectionBar } from "~/molecules/BulkSelectionBar";
import { FilterToggleButton } from "~/molecules/FilterToggleButton";
import HeadingPage from "~/molecules/HeadingPage";
import NoResult from "~/molecules/NoResult";
import { Toolbar } from "~/molecules/Toolbar";
import { OffboardWorkflowWarning } from "~/molecules/OffboardWorkflowWarning";
import { ActiveFilterChips } from "~/organisms/filters/ActiveFilterChips";
import { FilterableContentLayout } from "~/organisms/filters/FilterableContentLayout";
import { SelectFiltersPanel } from "~/organisms/filters/SelectFiltersPanel";
import { OffboardDocumentsTable } from "~/pages/offboard-user/components/OffboardDocumentsTable";
import { useOffboardUserPage } from "~/pages/offboard-user/useOffboardUserPage";

export default function OffboardUserPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const {
    offboardedUserIdParam,
    isLoadingRoles,
    hasData,
    offboardedUserId,
    offboardedUser,
    userOptions,
    filters,
    activeFilterChips,
    handleSelectFilter,
    filteredDocuments,
    selection,
    isSubmitting,
    canDisableUser,
    onDisableUser,
    getManagerId,
    getCellError,
    setManager,
  } = useOffboardUserPage();

  if (offboardedUserIdParam === undefined) {
    return null;
  }

  if (isLoadingRoles) {
    return <CenterSpinner />;
  }

  if (!hasData) {
    return <NoResult />;
  }

  return (
    <div className="container space-y-4 py-7">
      <HeadingPage
        title={
          offboardedUser
            ? `${t(($) => $.settings.userManagement.disableUser)}: ${printFullName(offboardedUser)}`
            : t(($) => $.settings.userManagement.disableUser)
        }
      />
      <p className="text-muted-foreground">
        {t(($) => $.settings.userManagement.offboardInstruction)}
      </p>
      <OffboardWorkflowWarning />
      <Toolbar
        left={
          <>
            <FilterToggleButton
              isOpen={isFiltersOpen}
              onToggle={() => setIsFiltersOpen((current) => !current)}
            />
            <BulkSelectionBar
              count={selection.selectedIds.size}
              onUnselectAll={selection.clearSelection}
              disabled={isSubmitting}
            />
          </>
        }
        right={
          <>
            <Button
              variant="outline"
              disabled={isSubmitting}
              onClick={() => void navigate("/users")}
            >
              {t(($) => $.commons.buttons.cancel)}
            </Button>
            <Button
              variant="destructive"
              disabled={isSubmitting || !canDisableUser}
              onClick={onDisableUser}
            >
              {t(($) => $.settings.userManagement.disableUser)}
            </Button>
          </>
        }
      />
      <FilterableContentLayout
        contentClassName="space-y-1"
        filters={
          <SelectFiltersPanel
            filters={filters}
            onSelectFilter={handleSelectFilter}
          />
        }
        isFiltersOpen={isFiltersOpen}
        sidebarLabel={t(($) => $.commons.filter.title)}
      >
        <ActiveFilterChips items={activeFilterChips} />
        <OffboardDocumentsTable
          documents={filteredDocuments}
          offboardedUserId={offboardedUserId}
          userOptions={userOptions}
          selection={selection}
          disabled={isSubmitting}
          getManagerId={getManagerId}
          getCellError={getCellError}
          onManagerChange={setManager}
        />
      </FilterableContentLayout>
    </div>
  );
}
